import axios from './axios';
import {
  AxiosError,
  AxiosResponse,
  Canceler,
  CancelToken as TypeCancelToken,
} from 'axios';

import store from 'configureStore';
import { actions as messageActions } from 'reducer/messages';
import { actions as appActions } from 'reducer/app';
import { actions as userActions } from 'reducer/user';

import history from './history';
import { Capacitor } from '@capacitor/core';
import { StatusType, StatusProps } from 'types/requestStatuses';

const CancelToken = axios.CancelToken;
export const source = CancelToken.source();

export let cancel: Array<{ c: Canceler; reqPath: string[] }>;

type RequestOptions = {
  responseType?: string;
  handleNoResponse?: boolean;
  type?: MIMEType;
  storeToken?: boolean;
  xTimesone?: string;
};

type MIMEType = 'json' | 'file' | 'text' | 'csv' | 'zip' | 'html' | 'stream'

const defaultOptions: RequestOptions = {
  handleNoResponse: true,
  type: 'json',
  storeToken: true,
};

export const errorCatching = (error: any, settings: RequestOptions) => {
  if (!error.response) {
    if (Capacitor?.isNative) {
      history.push('/no-connection');
      return null;
    }
    store.dispatch(appActions.toggleSpinner({ value: false }));
    if (settings.handleNoResponse) {
      store.dispatch(
        messageActions.addToStack({
          key: Math.random()
            .toString(32)
            .slice(0, 7),
          messages: ['Ошибка'],
        })
      );
      history.push('/server-error');
    }
  }

  switch (error?.response?.status) {
    case 400:
      store.dispatch(
        messageActions.addToStack({
          key: error.response.data.key,
          messages: [...error.response.data.messages],
        })
      );
      return error.response;
    case 401:
      store.dispatch(userActions.logOutSuccess());
      localStorage.removeItem('token');
      history.push('/');
      break;
    case 403:
      store.dispatch(
        messageActions.addToStack({
          key: error.response.data.key,
          messages: ['Отказано в доступе'],
        })
      );
      history.push('/access-denied');
      break;
    case 404:
      store.dispatch(
        messageActions.addToStack({
          key: Math.random()
            .toString(32)
            .slice(0, 7),
          messages:
            error.response &&
            error.response.data &&
            error.response.data.messages
              ? [...error.response.data.messages]
              : ['Не найдено'],
        })
      );
      break;
    case 500:
      store.dispatch(
        messageActions.addToStack({
          key: error.response.data.key,
          messages: error.response.data.messages?.length ? [...error.response.data.messages] : ['Внутренняя ошибка сервера'],
        })
      );
      break;
    case 502:
      store.dispatch(
        messageActions.addToStack({
          key: error.response.data.key,
          messages: ['Внутренняя ошибка сервера'],
        })
      );
      break;
    case 503:
      store.dispatch(
        messageActions.addToStack({
          key: error.response.data.key,
          messages: ['Сервис временно недоступен'],
        })
      );
      break;
  }
  return null;
};

const getOptionsType = (type?: MIMEType, charset?: string) => {
  switch (type) {
    case 'stream':
      return 'application/octet-stream'
    case 'zip':
      return 'application/zip'
    case 'csv':
      return `text/csv${charset ? `;charset=${charset}` : ''}`
    case 'text':
      return `text/plain${charset ? `;charset=${charset}` : ''}`
    case 'html':
      return `text/html${charset ? `;charset=${charset}` : ''}`
    case 'file':
      return 'multipart/form-data'
    default:
      return 'application/json'
  }
}

const getCommonPageId = (pageId: string): string => {
  if (pageId.includes('/video/online')) {
    return '/video/online';
  } else if (pageId.includes('/video/requests/create')) {
    return '/video/requests';
  }

  return pageId;
};

const getOptions = (pageId?: string[], options?: RequestOptions) => {
  return {
    responseType: options?.responseType,
    credentials: 'same-origin',
    headers: {
      'Content-Type': getOptionsType(options?.type),
      auth: localStorage.getItem('token'),
      token: localStorage.getItem('token'),
      'X-Timezone-Offset': -(new Date).getTimezoneOffset(),
      'Page-Id': getCommonPageId(pageId && pageId.length ? pageId[0] : history.location.pathname),
      'X-Timezone': options?.xTimesone || undefined
    },
    cancelToken: new CancelToken((c) =>
      cancel
        ? cancel.push({
            c,
            reqPath: pageId ? pageId : [history.location.pathname],
          })
        : (cancel = [
            {
              c,
              reqPath: pageId ? pageId : [],
            },
          ])
    ),
  };
};

const successResponse = (res: { data: any }) => res.data;
const rejectedResponse = (options: RequestOptions) => (error: any) =>
  axios.isCancel(error) ? null : errorCatching(error, options);

export const secureRequest = <Response = any>(
  url: string,
  pageId: string[] = [history.location.pathname],
  options: RequestOptions = defaultOptions
) => {
  return axios
    .get(url, getOptions(pageId, options))
    .then((res) => {
      if (res.headers.token && options.storeToken && store.getState().user.isLogged) {
        localStorage.setItem('token', res.headers.token);
      }
      return successResponse(res) as Response;
    })
    .catch((error) => rejectedResponse(options)(error) as Response);
};

export const postRequest = (url: string) => (body: object) => {
  return axios
    .post(url, body)
    .then((res) => {
      return res.data;
    });
};

export const securePostBlobDownloadRequest = (
  url: string,
  pageId: string[] = [history.location.pathname],
  options: RequestOptions = defaultOptions
) => (body: object) => {
  return axios
    .post(url, body, {
      ...getOptions(pageId, options), responseType: 'blob',
    })
    .catch(rejectedResponse(options));
};

export const secureBlobDownloadRequest = <Response = any>(
  url: string,
  pageId: string[] = [history.location.pathname],
  options: RequestOptions = defaultOptions
) => {
  return axios
    .get(url, { ...getOptions(pageId, options), responseType: 'blob' },)
    .then((res) => {
      if (res.headers.token && options.storeToken && store.getState().user.isLogged) {
        localStorage.setItem('token', res.headers.token);
      }
      return res;
    })
    .catch((error) => rejectedResponse(options)(error) as Response);
};

export const securePostRequest = (
  url: string,
  pageId: string[] = [history.location.pathname],
  options: RequestOptions = defaultOptions
) => (body: object) => {
  return axios
    .post(url, body, {
      ...getOptions(pageId, options),
    })
    .then(successResponse)
    .catch(rejectedResponse(options));
};

export const putRequest = (
  url: string,
  pageId: string[] = [history.location.pathname],
  options: RequestOptions = defaultOptions
) => (body: object) => {
  return axios
    .put(url, body, {
      ...getOptions(pageId, options),
    })
    .then(successResponse)
    .catch(rejectedResponse(options));
};

export const deleteRequest = (
  url: string,
  pageId: string[] = [history.location.pathname],
  options: RequestOptions = defaultOptions
) => {
  return axios
    .delete(url, {
      ...getOptions(pageId, options),
    })
    .then(successResponse)
    .catch(rejectedResponse(options));
};

export const deleteRequestBody = (
  url: string,
  pageId: string[] = [history.location.pathname],
  options: RequestOptions = defaultOptions
) => (body: object) => {
  return axios
    .delete(url, {data: body,
      ...getOptions(pageId, options),
    })
    .then(successResponse)
    .catch(rejectedResponse(options));
};

export const videoSecureRequest = (url: string) => {
  const source = CancelToken.source();

  const canceler = source.cancel;
  const ctoken = source.token;

  return {
    cancel: canceler,
    request: axios
      .get(url, {
        ...getCancelableOption(ctoken),
      })
      .then(({ data }: AxiosResponse) => ({
        isSuccess: true,
        data,
      }))
      .catch(({ response }: AxiosError) => ({
        data: response ? response.data : null,
        isSuccess: false,
      })),
  };
};

export const cancelableSecureRequest = (url: string) => {
  let canceler: Canceler | undefined;
  const ctoken = new CancelToken((c) => (canceler = c));
  return {
    cancel: canceler,
    request: axios
      .get(url, {
        ...getCancelableOption(ctoken),
      })
      .then((response: AxiosResponse) => ({
        isSuccess: true,
        data: response.data,
      }))
      .catch((error: AxiosError) => ({
        data: error.response ? error.response.data : null,
        isSuccess: false,
      })),
  };
};

const getCancelableOption = (ctoken: TypeCancelToken, options?: RequestOptions) => ({
  credentials: 'same-origin',
  headers: {
    Accept: 'application/json',
    'Content-Type': getOptionsType(options?.type),
    auth: localStorage.getItem('token'),
  },
  cancelToken: ctoken,
});

export const cancelableSecurePostRequest = (url: string, pageId: string, options?: RequestOptions) => (
  body: object
) => {
  let canceler: Canceler | undefined;
  const ctoken = new CancelToken((c) => (canceler = c));
  return {
    cancel: canceler!,
    request: axios
      .post(url, body, {
        ...getCancelableOption(ctoken, options),
      })
      .then((res) => res.data)
      .catch((res) => null),
  };
};

export const stringifySearchParams = (
  data: Record<string, unknown>
): string => {
  Object.keys(data).forEach((key) => {
    if (typeof data[key] === 'object') {
      data[key] = JSON.stringify(data[key]);
    }
  });

  return new window.URLSearchParams(
    data as { [key: string]: string }
  ).toString();
};

export const getRequestStatus = (status: StatusType, props: Partial<StatusProps> = {}): StatusType => {
  if (status === StatusType.PERFORMED) {
    return props.viewed
           ? StatusType.VIEWED
           : StatusType.PERFORMED;
  }

  return status;
}
